import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Theme,
} from '@material-ui/core'
import { Container, Selector } from '../../../components'
import {
    IdBoolMap,
    ModelMap,
    Service,
    convertListToMap,
    convertMapToList,
    getTotalPriceExpense,
} from '../../../models'
import { WorkorderResponse } from '../../../store'
import { InvoiceWorkorderCard } from '../VendorSendInvoiceModal/InvoiceWorkorderCard'
import { toast } from 'react-toastify'
import { currencyFormatter } from '../../../helpers'
import { axiosInstance } from '../../../helpers'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    refreshData: () => void
    workorderList: WorkorderResponse[] | null
}

export interface SendInvoiceRequestApartment {
    vendor_id: number
    workorders: number[]
}

export const ApartmentSendInvoice = (props: Props) => {
    const { theme, refreshData, workorderList } = props

    // Include all work orders with vendor and price - do not filter by schedule
    // (work orders without a schedule should be included for invoicing)
    const workordersWithVendor = useMemo(
        () =>
            workorderList?.filter(
                (wo) =>
                    wo.vendor_id != null &&
                    wo.vendor_name != null &&
                    getTotalPriceExpense(wo) > 0,
            ) ?? [],
        [workorderList],
    )

    const [openDialog, setOpenDialog] = useState(false)
    const [loading, setLoading] = useState(false)

    const [selectedServiceId, setSelectedServiceId] = useState(ALL_SERVICES)
    const [selectedVendorId, setSelectedVendorId] = useState(NO_VENDOR)

    const [selectedWorkorderMap, setSelectedWorkorderMap] = useState<IdBoolMap>(
        {},
    )

    useEffect(() => {
        if (workordersWithVendor.length > 0) {
            const firstVendorId = workordersWithVendor[0].vendor_id!
            setSelectedVendorId((prev) =>
                prev === NO_VENDOR ||
                !workordersWithVendor.some((wo) => wo.vendor_id === prev)
                    ? firstVendorId
                    : prev,
            )
        } else {
            setSelectedVendorId(NO_VENDOR)
        }
    }, [workordersWithVendor])

    const filteredWorkorders = useMemo(() => {
        return workordersWithVendor.filter((wo) => {
            const serviceValid =
                selectedServiceId === ALL_SERVICES ||
                selectedServiceId === wo.service_id
            const vendorValid = wo.vendor_id === selectedVendorId

            console.log('wo', wo)
            console.log('serviceValid', serviceValid)
            console.log('vendorValid', vendorValid)

            return serviceValid && vendorValid
        })
    }, [workordersWithVendor, selectedServiceId, selectedVendorId])

    const { vendorList, serviceList } = useMemo(() => {
        const vendorMap: ModelMap<{
            id: number
            name: string
            amount: number
        }> = {}
        const serviceMap: ModelMap<Service> = {}
        serviceMap[initialService.id] = initialService

        workordersWithVendor.forEach((wo) => {
            if (wo.service_id && !serviceMap[wo.service_id]) {
                serviceMap[wo.service_id] = {
                    id: wo.service_id ?? -1,
                    name: wo.service_name ?? '',
                    color: wo.service_color ?? '',
                    order: 1,
                    service_category: wo.service_category,
                }
            }

            if (wo.vendor_id != null && !vendorMap[wo.vendor_id]) {
                vendorMap[wo.vendor_id] = {
                    id: wo.vendor_id,
                    name: wo.vendor_name ?? '',
                    amount: workordersWithVendor.filter(
                        (workorder) => workorder.vendor_id === wo.vendor_id,
                    ).length,
                }
            }
        })

        return {
            vendorList: convertMapToList(vendorMap),
            serviceList: convertMapToList(serviceMap),
        }
    }, [workordersWithVendor])

    const selectedWorkorderIds = useMemo(
        () =>
            Object.keys(selectedWorkorderMap).reduce<number[]>((prev, id) => {
                if (id === 'length') return prev
                const numID = Number(id)
                if (selectedWorkorderMap[numID] === true) {
                    return prev.concat(numID)
                }
                return prev
            }, []),
        [selectedWorkorderMap],
    )

    const selectedWorkordersLength = selectedWorkorderIds.length
    const allChecked = selectedWorkordersLength === filteredWorkorders.length
    const atLeastOneChecked = selectedWorkordersLength > 0

    const invoiceTotal = useMemo(() => {
        let total = 0
        const workorderMap = convertListToMap(filteredWorkorders)
        selectedWorkorderIds.forEach((id) => {
            const wo = workorderMap[id]
            if (wo) total += getTotalPriceExpense(wo)
        })
        return total
    }, [filteredWorkorders, selectedWorkorderIds])

    const handleSelectAll = useCallback(() => {
        if (selectedWorkordersLength > 0) {
            setSelectedWorkorderMap({})
        } else {
            setSelectedWorkorderMap(
                filteredWorkorders.reduce((map, wo) => {
                    const validCost = getTotalPriceExpense(wo) > 0
                    return { ...map, [wo.id]: validCost }
                }, {}),
            )
        }
    }, [selectedWorkordersLength, filteredWorkorders])

    const handleWorkorderToggle = useCallback((woId: number) => {
        setSelectedWorkorderMap((prev) => {
            const next = { ...prev }
            if (next[woId]) {
                next[woId] = false
            } else {
                next[woId] = true
            }
            return next
        })
    }, [])

    const handleSendInvoice = () => {
        const request: SendInvoiceRequestApartment = {
            vendor_id: selectedVendorId,
            workorders: selectedWorkorderIds,
        }
        setLoading(true)
        axiosInstance
            .post('invoice/', request)
            .then((res) => {
                const badWorkorderCount: number =
                    res.data?.bad_wo_list?.length ?? 0
                if (badWorkorderCount > 0) {
                    toast.error(
                        `${badWorkorderCount} Work Orders did not have pricing configured`,
                    )
                }
                toast.success(
                    `${
                        selectedWorkordersLength - badWorkorderCount
                    } Workorders invoiced successfully`,
                )
                refreshData()
                setSelectedWorkorderMap({})
                setOpenDialog(false)
            })
            .catch(() => {
                toast.error('Error sending invoice')
            })
            .finally(() => setLoading(false))
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                overflow: 'auto',
            }}
        >
            <Container style={{ marginBottom: theme.spacing(1) }}>
                <FormControlLabel
                    style={{ marginLeft: theme.spacing(1) }}
                    control={
                        <Checkbox
                            checked={allChecked}
                            style={{
                                color: theme.palette.secondary.main,
                            }}
                            indeterminate={atLeastOneChecked}
                            onChange={handleSelectAll}
                        />
                    }
                    label={atLeastOneChecked ? 'Remove All' : 'Select All'}
                />
                <Selector
                    label="Vendor"
                    data={vendorList}
                    searchable
                    currentValue={selectedVendorId}
                    onChange={(e) => {
                        setSelectedVendorId(e.target.value as number)
                        setSelectedWorkorderMap({})
                    }}
                    getDisplayString={(vendor) =>
                        `${vendor.name} (${vendor.amount})`
                    }
                    customStyle={{
                        formControl: {
                            margin: theme.spacing(1),
                            minWidth: '200px',
                        },
                    }}
                />
                <Selector
                    label="Service"
                    data={serviceList}
                    searchable
                    currentValue={selectedServiceId}
                    onChange={(e) => {
                        setSelectedServiceId(e.target.value as number)
                        setSelectedWorkorderMap({})
                    }}
                    getDisplayString={(service) => service.name}
                    customStyle={{
                        formControl: {
                            margin: theme.spacing(1),
                            minWidth: '200px',
                        },
                    }}
                />
            </Container>
            <Container
                style={{
                    flexDirection: 'column',
                    overflow: 'scroll',
                    flex: 1,
                    width: '100%',
                }}
            >
                {filteredWorkorders.map((wo) => (
                    <WorkorderRow
                        key={wo.id}
                        workorder={wo}
                        theme={theme}
                        isChecked={selectedWorkorderMap[wo.id] === true}
                        onToggle={handleWorkorderToggle}
                        disabled={getTotalPriceExpense(wo) === 0}
                    />
                ))}
            </Container>
            <Container
                style={{
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    padding: theme.spacing(2, 1, 2, 0),
                }}
            >
                <Button
                    variant="contained"
                    style={{
                        backgroundColor: theme.palette.primary.dark,
                        color: 'white',
                        textTransform: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={() => setOpenDialog(true)}
                    disabled={selectedWorkordersLength === 0}
                >
                    {`Send Invoice (${selectedWorkordersLength})`}
                </Button>
            </Container>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle id="alert-dialog-title">
                    Confirm Sending Invoice
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        You are about to invoice ({selectedWorkordersLength})
                        Workorders for a total of{' '}
                        {currencyFormatter.format(invoiceTotal)}
                    </DialogContentText>

                    {selectedWorkordersLength < 5 && (
                        <DialogContentText
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                marginTop: theme.spacing(2),
                            }}
                        >
                            {`* This is an unusually small invoice. Unless you're
                            sure you only want to invoice ${selectedWorkordersLength}
                            workorders consider adding some more to simplify
                            the payment process.`}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => setOpenDialog(false)}
                    >
                        Cancel
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={handleSendInvoice}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={25} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

const ALL_SERVICES = -1
const NO_VENDOR = -1
const CHECK_BOX_W = 42
const initialService = {
    id: ALL_SERVICES,
    name: 'All',
    color: '#000000',
    order: -1,
}

interface WorkorderRowProps {
    workorder: WorkorderResponse
    theme: Theme
    isChecked: boolean
    onToggle: (woId: number) => void
    disabled: boolean
}

const WorkorderRow = React.memo((props: WorkorderRowProps) => {
    const { workorder, theme, isChecked, onToggle, disabled } = props
    return (
        <Container style={{ width: '100%' }}>
            <Checkbox
                style={{ width: CHECK_BOX_W }}
                checked={isChecked}
                onChange={() => onToggle(workorder.id)}
                disabled={disabled}
            />
            <InvoiceWorkorderCard
                workorder={workorder}
                theme={theme}
                fullWidth
            />
        </Container>
    )
})
